import { useCallback, useMemo, useSyncExternalStore } from 'react';

// A preference kept in localStorage that every component using the same key
// sees at once: in this tab through our own listeners, in other tabs through
// the storage event. useStoredState is per component; use this when two
// places (the sidebar button and the Account page) change the same setting.

const listeners = new Map<string, Set<() => void>>();
/** Used when localStorage is unavailable (private windows, blocked storage). */
const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return memory.get(key) ?? null;
  }
}

function write(key: string, raw: string) {
  try {
    localStorage.setItem(key, raw);
  } catch {
    memory.set(key, raw);
  }
  listeners.get(key)?.forEach((fn) => fn());
}

function subscribe(key: string, fn: () => void): () => void {
  let set = listeners.get(key);
  if (!set) listeners.set(key, (set = new Set()));
  set.add(fn);
  const onStorage = (e: StorageEvent) => e.key === key && fn();
  window.addEventListener('storage', onStorage);
  return () => {
    set.delete(fn);
    window.removeEventListener('storage', onStorage);
  };
}

export function usePref<T>(key: string, initial: T): [T, (next: T | ((prev: T) => T)) => void] {
  const raw = useSyncExternalStore(
    useCallback((fn) => subscribe(key, fn), [key]),
    () => read(key),
    () => null,
  );
  const value = useMemo<T>(() => {
    if (raw === null) return initial;
    try {
      return JSON.parse(raw) as T;
    } catch {
      return initial;
    }
    // `initial` is the default only; a new object each render must not re-parse.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      const current = read(key);
      let prev = initial;
      try {
        if (current !== null) prev = JSON.parse(current) as T;
      } catch {
        // keep the default
      }
      write(key, JSON.stringify(typeof next === 'function' ? (next as (p: T) => T)(prev) : next));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  return [value, set];
}

/** Page zoom levels offered in the display settings. */
export const ZOOM_LEVELS = [0.8, 0.9, 1, 1.1, 1.25, 1.5] as const;
export const ZOOM_KEY = 'layout.zoom';
/** Sidebar: the same key the sidebar's own collapse button uses. */
export const RAIL_KEY = 'layout.side_rail';

/** A stored zoom that isn't one of the offered levels falls back to 100%. */
export const validZoom = (z: unknown): number => (typeof z === 'number' && (ZOOM_LEVELS as readonly number[]).includes(z) ? z : 1);
